import React, { useCallback, useEffect, useState } from 'react';
import CopilotDaySummary, { CopilotStatus } from './CopilotDaySummary';
import { CurrentUser } from './CopilotTimeSuggestions';
import ClippyIcon from './ClippyIcon';
import { getRandomClippyQuote } from './clippyQuotes';
import './CopilotAssistant.css';

const BUBBLE_DISMISSED_KEY = 'copilotBubbleDismissed';
const GREETING_DURATION_MS = 10000;

// Module-level so the greeting shows once per app load, not on every tab switch back to the calendar
let hasGreeted = false;

const formatHours = (hours: number): string => hours.toLocaleString('sv-SE', { maximumFractionDigits: 2 });

export const describeSuggestions = (status: CopilotStatus): string =>
  `${status.pendingCount} förslag, ${formatHours(status.pendingHours)} h`;

interface CopilotAssistantProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentUser: CurrentUser | null;
  onEntriesAdded?: () => void;
  onStatusChange?: (status: CopilotStatus) => void;
}

// Floating Clippy that opens a side panel with Copilot's time report suggestions.
// The panel stays mounted while closed so edits and background loading survive opening/closing it.
const CopilotAssistant: React.FC<CopilotAssistantProps> = ({
  isOpen,
  onOpenChange,
  currentUser,
  onEntriesAdded,
  onStatusChange,
}) => {
  const [status, setStatus] = useState<CopilotStatus>({ loading: false, pendingCount: 0, pendingHours: 0, createdAt: null });
  const [bubbleDismissedFor, setBubbleDismissedFor] = useState<string | null>(() => {
    try {
      return localStorage.getItem(BUBBLE_DISMISSED_KEY);
    } catch {
      return null;
    }
  });
  const [greeting, setGreeting] = useState<string | null>(() => (hasGreeted ? null : getRandomClippyQuote()));

  // Clippy shares a thought on time when the app opens, then makes room for the suggestions bubble
  useEffect(() => {
    if (!greeting) return;
    hasGreeted = true;
    const timer = setTimeout(() => setGreeting(null), GREETING_DURATION_MS);
    return () => clearTimeout(timer);
  }, [greeting]);

  const handleStatusChange = useCallback(
    (next: CopilotStatus) => {
      setStatus(next);
      onStatusChange?.(next);
    },
    [onStatusChange]
  );

  // The bubble is shown once per Copilot answer
  const dismissBubble = useCallback(() => {
    if (!status.createdAt) return;
    setBubbleDismissedFor(status.createdAt);
    try {
      localStorage.setItem(BUBBLE_DISMISSED_KEY, status.createdAt);
    } catch {
      // Not persisted - the bubble just shows again next visit
    }
  }, [status.createdAt]);

  const open = () => {
    dismissBubble();
    onOpenChange(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onOpenChange]);

  const showBubble =
    !isOpen && !greeting && !status.loading && status.pendingCount > 0 && status.createdAt !== bubbleDismissedFor;

  return (
    <>
      {!isOpen && (
        <div className="clippy-fab-wrapper">
          {greeting && (
            <div className="clippy-bubble" role="status">
              <button className="clippy-bubble-close" onClick={() => setGreeting(null)} aria-label="Stäng">×</button>
              <button className="clippy-bubble-body" onClick={() => setGreeting(null)}>
                {greeting}
              </button>
            </div>
          )}
          {showBubble && (
            <div className="clippy-bubble" role="status">
              <button className="clippy-bubble-close" onClick={dismissBubble} aria-label="Stäng">×</button>
              <button className="clippy-bubble-body" onClick={open}>
                Det ser ut som att du har jobbat idag!
                <strong>{describeSuggestions(status)}</strong>
              </button>
            </div>
          )}
          <button
            className="clippy-fab"
            onClick={open}
            aria-label="Öppna Copilots förslag på tidrapport"
            title="Min dag enligt Copilot"
          >
            <ClippyIcon busy={status.loading} height={44} />
            {status.pendingCount > 0 && <span className="clippy-fab-badge">{status.pendingCount}</span>}
          </button>
        </div>
      )}

      <div
        className={`copilot-drawer-backdrop${isOpen ? ' is-open' : ''}`}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      <aside
        className={`copilot-drawer${isOpen ? ' is-open' : ''}`}
        // Keeps the closed (off-screen) panel out of tab order and screen readers
        inert={!isOpen}
        aria-label="Copilots förslag på tidrapport"
      >
        <CopilotDaySummary
          currentUser={currentUser}
          onEntriesAdded={onEntriesAdded}
          onStatusChange={handleStatusChange}
          onClose={() => onOpenChange(false)}
        />
      </aside>
    </>
  );
};

export default CopilotAssistant;
